import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../services/api";
import BottomNav from "../components/BottomNav";
import { buildWhatsAppUrl, openExternalLink, queueDashboardNotice } from "../utils/appInteractions";

function Deposit() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  const [amount, setAmount]     = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({ min_deposit: "300", whatsapp_number: "", deposit_mode: "gateway", deposit_qr_url: "/qr.png", deposit_upi_id: "" });
  const [waStep, setWaStep]     = useState("amount");
  const [qrStep, setQrStep]     = useState("amount");

  const pgStatus = searchParams.get("pgstatus");
  const pgRef    = searchParams.get("ref");
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [gatewayAmount, setGatewayAmount] = useState(0);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!user?.id) { navigate("/login"); return; }
    API.get("/settings/public").then((r) => setSettings((c) => ({ ...c, ...(r.data || {}) }))).catch(() => {});
  }, [navigate, user]);

  useEffect(() => {
    if (!user?.id) return;
    API.post("/payment-intents", { user_id: user.id, event_type: "visit", source: "deposit" }).catch(() => {});
  }, [user?.id]);

  const pollGateway = useCallback(async () => {
    if (!pgRef || !user?.id) return;
    try {
      const res = await API.get(`/check-gateway-deposit/${pgRef}`, { params: { user_id: user.id } });
      const s = String(res.data?.status || "pending").toLowerCase();
      setGatewayAmount(Number(res.data?.amount || 0));
      if (s === "approved") {
        setGatewayStatus("approved");
      } else if (s === "rejected") {
        setGatewayStatus("rejected");
      } else {
        setGatewayStatus("pending");
        pollCount.current += 1;
        if (pollCount.current < 10) setTimeout(pollGateway, 3000);
      }
    } catch {
      setGatewayStatus("pending");
    }
  }, [pgRef, user?.id]);

  useEffect(() => {
    if (pgStatus === "done" && pgRef) {
      setGatewayStatus("pending");
      pollGateway();
    }
  }, [pgStatus, pgRef, pollGateway]);

  const minDeposit      = Math.max(1, Number(settings.min_deposit || 300) || 300);
  const normalizedAmount = amount === "" ? "" : String(Math.max(minDeposit, Number(amount || 0) || 0));
  const rawMode         = String(settings.deposit_mode || "gateway").toLowerCase();
  const depositMode     = rawMode === "whatsapp" ? "whatsapp" : rawMode === "qr" ? "qr" : "gateway";
  const depositUpiId    = String(settings.deposit_upi_id || "").trim();
  const depositQrUrl    = settings.deposit_qr_url || "/qr.png";

  const updateAmount = (v) => setAmount(String(v || "").replace(/[^\d]/g, ""));

  const ensureAmount = () => {
    if (!amount) { alert("Enter amount"); return false; }
    if (Number(amount) < minDeposit) { setAmount(String(minDeposit)); alert(`Minimum deposit is Rs ${minDeposit}`); return false; }
    return true;
  };

  const handleGatewayPay = async () => {
    if (!user?.id) { alert("Please login first"); return; }
    if (!ensureAmount()) return;
    setSubmitting(true);
    try {
      const res = await API.post("/initiate-gateway-deposit", { user_id: user.id, amount: normalizedAmount });
      if (res.data?.payment_link) {
        window.location.href = res.data.payment_link;
      } else {
        alert(res.data?.message || "Could not create payment. Try again.");
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Payment gateway error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };


  const openWhatsApp = () => {
    const phone = getWhatsappPhone(settings.whatsapp_number);
    if (!phone) { alert("WhatsApp number is not configured"); return; }
    const url = buildWhatsAppUrl(phone, `I want to deposit Rs ${normalizedAmount || amount}`);
    if (!url) { alert("WhatsApp number is not configured"); return; }
    openExternalLink(url);
  };

  const handleQrSubmit = async () => {
    if (!user?.id) { alert("Please login first"); return; }
    if (!ensureAmount()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("user_id", user.id);
      formData.append("amount", normalizedAmount);
      await API.post("/deposit", formData, { headers: { "Content-Type": "multipart/form-data" } });
      queueDashboardNotice(`Deposit request for Rs ${Number(normalizedAmount || 0).toFixed(2)} submitted. Will be credited after verification.`);
      setAmount("");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      alert(err?.response?.data?.message || "Error submitting deposit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsAppSubmit = async () => {
    if (!user?.id) { alert("Please login first"); return; }
    if (!ensureAmount()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("user_id", user.id);
      formData.append("amount", normalizedAmount);
      await API.post("/deposit", formData, { headers: { "Content-Type": "multipart/form-data" } });
      queueDashboardNotice(`Deposit request for Rs ${Number(normalizedAmount || 0).toFixed(2)} submitted successfully.`);
      setAmount("");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      alert(err?.response?.data?.message || "Error submitting deposit");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Gateway return screen ───────────────────────────────────────────────────
  if (pgStatus === "done" || gatewayStatus) {
    return (
      <div style={page}>
        <section style={card}>
          {gatewayStatus === "approved" && (
            <div style={statusBox("#f0fdf4", "#16a34a")}>
              <CheckCircle2 size={48} color="#16a34a" />
              <h2 style={{ margin: "12px 0 4px", color: "#15803d" }}>Payment Successful!</h2>
              <p style={statusMsg}>Rs {gatewayAmount.toFixed(2)} has been credited to your wallet.</p>
              <button style={primaryBtn("#16a34a")} onClick={() => navigate("/dashboard", { replace: true })}>Go to Dashboard</button>
            </div>
          )}
          {gatewayStatus === "rejected" && (
            <div style={statusBox("#fef2f2", "#dc2626")}>
              <XCircle size={48} color="#dc2626" />
              <h2 style={{ margin: "12px 0 4px", color: "#dc2626" }}>Payment Failed</h2>
              <p style={statusMsg}>Your payment could not be processed. Please try again.</p>
              <button style={primaryBtn("#dc2626")} onClick={() => navigate("/deposit", { replace: true })}>Try Again</button>
            </div>
          )}
          {(gatewayStatus === "pending" || !gatewayStatus) && (
            <div style={statusBox("#eff6ff", "#2563eb")}>
              <Loader2 size={48} color="#2563eb" style={{ animation: "spin 1s linear infinite" }} />
              <h2 style={{ margin: "12px 0 4px", color: "#1d4ed8" }}>Verifying Payment…</h2>
              <p style={statusMsg}>Please wait while we confirm your payment.</p>
              <button style={primaryBtn("#64748b")} onClick={() => navigate("/dashboard", { replace: true })}>Go to Dashboard</button>
            </div>
          )}
        </section>
        <BottomNav />
        <style>{spinCss}</style>
      </div>
    );
  }

  // ── Amount section (shared) ─────────────────────────────────────────────────
  const amountSection = (
    <>
      <label style={fieldLabel}>Enter Amount</label>
      <input
        style={amountInput}
        type="number"
        inputMode="numeric"
        min={minDeposit}
        placeholder={`Min Rs ${minDeposit}`}
        value={amount}
        onChange={(e) => updateAmount(e.target.value)}
        onBlur={() => { if (amount && Number(amount) < minDeposit) setAmount(String(minDeposit)); }}
      />
      <div style={quickGrid}>
        {[200, 500, 1000, 2000, 5000, 10000].filter((v) => v >= minDeposit).map((v) => (
          <button key={v} type="button" style={Number(normalizedAmount || 0) === v ? quickActive : quickChip} onClick={() => setAmount(String(v))}>
            ₹{v.toLocaleString("en-IN")}
          </button>
        ))}
      </div>
      <p style={hintTxt}>Minimum deposit: ₹{minDeposit}</p>
    </>
  );

  // ── QR / UPI app mode ───────────────────────────────────────────────────────
  if (depositMode === "qr") {
    const upiLink = (scheme) =>
      `${scheme}?pa=${encodeURIComponent(depositUpiId)}&pn=${encodeURIComponent("PlayOnline")}&am=${normalizedAmount || amount}&cu=INR&tn=Deposit`;
    const openUpiApp = (scheme) => {
      if (!depositUpiId) { alert("UPI ID not configured. Contact support."); return; }
      if (!ensureAmount()) return;
      window.location.href = upiLink(scheme);
      setTimeout(() => setQrStep("confirm"), 1500);
    };
    return (
      <div style={page}>
        <section style={card}>
          <div style={cardHeader("#fef9c3", "#fde68a")}>
            <div style={iconCircle("linear-gradient(135deg,#f59e0b,#d97706)", "rgba(245,158,11,0.3)")}>
              <svg viewBox="0 0 24 24" fill="#fff" width="26" height="26"><path d="M3 11l19-9-9 19-2-8-8-2z"/></svg>
            </div>
            <div>
              <h2 style={titleText}>Add Money</h2>
              <p style={{ ...headerCaption, color: "#d97706" }}>UPI / QR Payment</p>
            </div>
          </div>

          {qrStep === "amount" && (
            <>
              {amountSection}
              <button type="button" style={waBtn} onClick={() => { if (ensureAmount()) setQrStep("pay"); }}>
                Continue →
              </button>
            </>
          )}

          {qrStep === "pay" && (
            <>
              <div style={summaryBar}>
                <div>
                  <div style={summaryLbl}>Deposit Amount</div>
                  <div style={summaryAmt}>₹ {Number(normalizedAmount || 0).toLocaleString("en-IN")}</div>
                </div>
              </div>

              {/* QR Code */}
              <div style={qrWrap}>
                <img src={depositQrUrl} alt="Scan to pay" style={qrImg} />
                <p style={qrHint}>Scan with any UPI app</p>
              </div>

              {/* UPI App Buttons */}
              <p style={orDivider}>— OR pay with app —</p>
              <div style={upiAppRow}>
                <button type="button" style={gPayAppBtn} onClick={() => openUpiApp("tez://upi/pay")}>
                  <svg viewBox="0 0 48 48" width="26" height="26"><path fill="#4285F4" d="M24 9.5c3.5 0 6.6 1.3 9 3.4l6.7-6.7C35.8 2.5 30.3 0 24 0 14.7 0 6.9 5.5 3.1 13.5l7.8 6.1C12.7 13.5 17.9 9.5 24 9.5z"/><path fill="#34A853" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.7-2.1 5-4.4 6.5l6.8 5.3c4-3.7 6.3-9.2 6.3-16.3z"/><path fill="#FBBC05" d="M10.9 28.6c-.5-1.4-.8-2.9-.8-4.5s.3-3.1.8-4.5L3.1 13.5C1.1 17.2 0 21.5 0 26.1s1.1 8.9 3.1 12.5l7.8-10z"/><path fill="#EA4335" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-6.8-5.3c-2.1 1.4-4.8 2.2-9.1 2.2-6.1 0-11.3-4-13.1-9.5l-7.8 10C6.9 42.5 14.7 48 24 48z"/></svg>
                  Google Pay
                </button>
                <button type="button" style={paytmAppBtn} onClick={() => openUpiApp("paytmmp://pay")}>
                  <svg viewBox="0 0 48 48" width="26" height="26"><rect width="48" height="48" rx="10" fill="#002970"/><path fill="#00BAF2" d="M8 30V18h5v12H8zM15 18h5l4 7 4-7h5v12h-5v-7l-4 7-4-7v7h-5V18z"/></svg>
                  Paytm
                </button>
                <button type="button" style={phonePeAppBtn} onClick={() => openUpiApp("phonepe://pay")}>
                  <svg viewBox="0 0 48 48" width="26" height="26"><rect width="48" height="48" rx="10" fill="#5f259f"/><path fill="#fff" d="M24 8c-8.8 0-16 7.2-16 16s7.2 16 16 16c3.5 0 6.7-1.1 9.3-3l-2.8-3.5C28.8 35 26.5 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.3 0 6.2 1.3 8.4 3.4l3-3.4C32.6 9.4 28.5 8 24 8zm4 10h-6v12h4v-4h2c3.3 0 6-2.7 6-6s-2.7-2-6-2zm0 4h-2v-4h2c1.1 0 2 .9 2 2s-.9 2-2 2z"/></svg>
                  PhonePe
                </button>
              </div>

              <button
                type="button"
                style={submitting ? { ...submitBtn, opacity: 0.6 } : submitBtn}
                onClick={handleQrSubmit}
                disabled={submitting}
              >
                <CheckCircle2 size={18} />
                {submitting ? "Submitting..." : "I've Paid — Confirm Deposit"}
              </button>
              <p style={noteText}>After paying via QR or UPI app, tap Confirm. Your deposit will be credited after verification.</p>
            </>
          )}

          {qrStep === "confirm" && (
            <div style={statusBox("#f0fdf4", "#16a34a")}>
              <CheckCircle2 size={48} color="#16a34a" />
              <h2 style={{ margin: "12px 0 4px", color: "#15803d" }}>Payment Sent?</h2>
              <p style={statusMsg}>If you completed payment, tap Confirm below.</p>
              <button style={primaryBtn("#16a34a")} onClick={handleQrSubmit} disabled={submitting}>
                {submitting ? "Submitting..." : "Yes, I've Paid — Confirm"}
              </button>
              <button style={{ ...primaryBtn("#64748b"), marginTop: 8 }} onClick={() => setQrStep("pay")}>
                Back
              </button>
            </div>
          )}
        </section>
        <BottomNav />
        <style>{spinCss}</style>
      </div>
    );
  }

  // ── Gateway mode ────────────────────────────────────────────────────────────
  if (depositMode === "gateway") {
    return (
      <div style={page}>
        <section style={card}>
          <div style={cardHeader("#eff6ff", "#bfdbfe")}>
            <div style={iconCircle("linear-gradient(135deg,#4f46e5,#7c3aed)", "rgba(79,70,229,0.3)")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="26" height="26">
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
            </div>
            <div>
              <h2 style={titleText}>Add Money</h2>
              <p style={{ ...headerCaption, color: "#4f46e5" }}>Instant UPI deposit</p>
            </div>
          </div>
          {amountSection}
          <button
            type="button"
            style={submitting ? { ...upiBtn, opacity: 0.7 } : upiBtn}
            onClick={handleGatewayPay}
            disabled={submitting}
          >
            {submitting
              ? <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
              : <svg viewBox="0 0 24 24" fill="#fff" width="22" height="22"><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 14l-4-4 1.41-1.41L11 13.17l6.59-6.59L19 8l-8 8z"/></svg>
            }
            {submitting ? "Creating Payment…" : "Pay via UPI Gateway"}
          </button>
        </section>
        <BottomNav />
        <style>{spinCss}</style>
      </div>
    );
  }

  // ── WhatsApp mode ───────────────────────────────────────────────────────────
  return (
    <div style={page}>
      <section style={card}>
        <div style={cardHeader("#dcfce7", "#bbf7d0")}>
          <div style={iconCircle("#25D366", "rgba(37,211,102,0.3)")}>
            <svg viewBox="0 0 24 24" fill="#fff" width="26" height="26"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M11.998 0C5.373 0 0 5.373 0 11.998c0 2.117.554 4.1 1.523 5.819L.057 23.886a.5.5 0 0 0 .613.613l6.224-1.488A11.955 11.955 0 0 0 11.998 24C18.623 24 24 18.627 24 12.002 24 5.373 18.623 0 11.998 0zm0 21.818a9.809 9.809 0 0 1-5.021-1.378l-.36-.214-3.694.883.884-3.632-.234-.373A9.777 9.777 0 0 1 2.18 12.002c0-5.412 4.405-9.818 9.818-9.818 5.41 0 9.82 4.406 9.82 9.818 0 5.413-4.41 9.816-9.82 9.816z"/></svg>
          </div>
          <div>
            <h2 style={titleText}>Add Money</h2>
            <p style={{ ...headerCaption, color: "#16a34a" }}>Pay via WhatsApp</p>
          </div>
        </div>

        {amountSection}

        {waStep === "amount" && (
          <button type="button" style={waBtn} onClick={() => { if (ensureAmount()) setWaStep("pay"); }}>
            Continue →
          </button>
        )}

        {waStep === "pay" && (
          <>
            <div style={summaryBar}>
              <div>
                <div style={summaryLbl}>Deposit Amount</div>
                <div style={summaryAmt}>₹ {Number(normalizedAmount || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
            <button type="button" style={waBtn} onClick={openWhatsApp}>
              <svg viewBox="0 0 24 24" fill="#fff" width="20" height="20"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M11.998 0C5.373 0 0 5.373 0 11.998c0 2.117.554 4.1 1.523 5.819L.057 23.886a.5.5 0 0 0 .613.613l6.224-1.488A11.955 11.955 0 0 0 11.998 24C18.623 24 24 18.627 24 12.002 24 5.373 18.623 0 11.998 0zm0 21.818a9.809 9.809 0 0 1-5.021-1.378l-.36-.214-3.694.883.884-3.632-.234-.373A9.777 9.777 0 0 1 2.18 12.002c0-5.412 4.405-9.818 9.818-9.818 5.41 0 9.82 4.406 9.82 9.818 0 5.413-4.41 9.816-9.82 9.816z"/></svg>
              Send Payment on WhatsApp
            </button>
            <button type="button" style={submitting ? { ...submitBtn, opacity: 0.6 } : submitBtn} onClick={handleWhatsAppSubmit} disabled={submitting}>
              <CheckCircle2 size={18} />
              {submitting ? "Submitting..." : "I've Sent Payment — Submit"}
            </button>
            <p style={noteText}>After sending payment on WhatsApp, tap Submit. Your deposit will be credited after verification.</p>
          </>
        )}
      </section>
      <BottomNav />
    </div>
  );
}

function getWhatsappPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits;
}

const spinCss = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
const statusBox  = (bg, color) => ({ textAlign: "center", padding: "32px 16px", borderRadius: 20, background: bg, border: `1px solid ${color}30`, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 });
const statusMsg  = { color: "#475569", fontSize: 14, margin: 0 };
const primaryBtn = (bg) => ({ marginTop: 16, width: "100%", height: 52, border: 0, borderRadius: 14, background: bg, color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer" });

const page        = { minHeight: "100vh", background: "linear-gradient(160deg,#f0f9ff 0%,#f8fafc 100%)", padding: "16px 12px 90px", boxSizing: "border-box" };
const card        = { maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 24, padding: "20px 18px", boxShadow: "0 8px 32px rgba(0,0,0,0.08)", border: "1px solid #f1f5f9" };
const cardHeader  = (bg, border) => ({ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, padding: "14px 16px", borderRadius: 18, background: `linear-gradient(135deg,${bg},#fff)`, border: `1px solid ${border}` });
const iconCircle  = (bg, shadow) => ({ width: 52, height: 52, borderRadius: "50%", background: bg, display: "grid", placeItems: "center", flexShrink: 0, boxShadow: `0 4px 14px ${shadow}` });
const titleText   = { margin: 0, color: "#0f172a", fontSize: 20, fontWeight: 900 };
const headerCaption = { margin: "3px 0 0", fontSize: 12, fontWeight: 700 };
const fieldLabel  = { display: "block", marginBottom: 8, color: "#374151", fontWeight: 700, fontSize: 13 };
const amountInput = { width: "100%", height: 52, marginBottom: 12, padding: "0 16px", border: "1.5px solid #e2e8f0", borderRadius: 14, boxSizing: "border-box", fontSize: 18, fontWeight: 700, background: "#f8fafc", outline: "none", color: "#0f172a" };
const quickGrid   = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10 };
const quickChip   = { height: 38, border: "1.5px solid #e5e7eb", borderRadius: 10, background: "#f8fafc", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer" };
const quickActive = { ...quickChip, background: "#fef9c3", color: "#92400e", border: "1.5px solid #fbbf24" };
const hintTxt     = { margin: "0 0 16px", color: "#64748b", fontSize: 12 };
const upiBtn      = { width: "100%", height: 56, border: 0, borderRadius: 16, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontWeight: 900, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", boxShadow: "0 8px 24px rgba(79,70,229,0.30)" };
const waBtn       = { width: "100%", height: 52, border: 0, borderRadius: 14, background: "linear-gradient(135deg,#25D366,#149d4c)", color: "#fff", fontWeight: 900, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", marginBottom: 12, boxShadow: "0 8px 24px rgba(37,211,102,0.25)" };
const summaryBar  = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderRadius: 14, background: "linear-gradient(135deg,#1e1b4b,#312e81)", marginBottom: 14, color: "#fff" };
const summaryLbl  = { fontSize: 11, opacity: 0.7, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" };
const summaryAmt  = { fontSize: 22, fontWeight: 900, marginTop: 2 };
const submitBtn   = { width: "100%", height: 52, border: 0, borderRadius: 16, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontWeight: 900, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, cursor: "pointer", marginBottom: 14 };
const noteText    = { margin: 0, color: "#64748b", fontSize: 12, textAlign: "center", lineHeight: 1.6 };

const qrWrap      = { display: "flex", flexDirection: "column", alignItems: "center", margin: "16px 0 4px" };
const qrImg       = { width: "min(220px, 80%)", height: "min(220px, 80%)", objectFit: "contain", border: "2px solid #e2e8f0", borderRadius: 16, background: "#fff", padding: 8 };
const qrHint      = { margin: "8px 0 0", color: "#64748b", fontSize: 12, fontWeight: 700 };
const orDivider   = { textAlign: "center", color: "#94a3b8", fontSize: 12, fontWeight: 700, margin: "12px 0 10px", letterSpacing: "0.05em" };
const upiAppRow   = { display: "flex", gap: 8, marginBottom: 14, justifyContent: "center" };
const baseAppBtn  = { flex: 1, minHeight: 54, border: 0, borderRadius: 14, color: "#fff", fontWeight: 800, fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer", padding: "6px 4px" };
const gPayAppBtn  = { ...baseAppBtn, background: "linear-gradient(135deg,#4285F4,#34A853)" };
const paytmAppBtn = { ...baseAppBtn, background: "linear-gradient(135deg,#002970,#00BAF2)" };
const phonePeAppBtn = { ...baseAppBtn, background: "linear-gradient(135deg,#5f259f,#9b3bdb)" };

export default Deposit;
